import { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { BorderRadiusSmall } from 'autogen/design-tokens/tokens';
import type { ConnectedContentFullwidthModel } from 'autogen/swagger/all';
import { CustomActionDiscriminator } from 'autogen/swagger/all';
import type { Theme } from 'autogen/theming/types/themeTypes';
import HorizontalLayout from 'components/connectedContent/shared/horizontalLayout';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import { guardIsPageActionOfTypeCustomAction } from 'components/shared/Routing/components/shared/helpers';
import PageAction from 'components/shared/Routing/PageAction';
import { useOpenSurveyQuickView } from 'components/surveySystem/shared/hooks/useOpenSurveyQuickView';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import { rem } from 'modules/helpers/style';
import { postAdformClickTracking } from 'modules/tracking/adformClickTracking';
import track from 'modules/tracking/dataLayer';
import { invert } from 'polished';
import ThemeProvider from 'theming/ThemeProvider';
import { constants } from 'variables';

const StaticWrapper = styled.div`
    grid-column: 1 / -1;
    position: relative;
    width: 100%;
`;

const PageActionWrapper = styled(PageAction)`
    grid-column: 1 / -1;
    position: relative;
    width: 100%;
`;

export const TopLabel = styled.div<{ labelTextColor: string }>`
    position: absolute;
    top: ${rem(5)};
    left: ${rem(5)};
    z-index: 1;
    font-size: ${rem(10)};
    font-weight: 700;
    letter-spacing: 0;
    line-height: ${rem(12)};
    border-radius: ${BorderRadiusSmall};
    padding: 4px 8px;
    max-width: calc(100% - ${rem(10)});

    ${({ labelTextColor }) =>
        labelTextColor &&
        css`
            color: ${labelTextColor};
            background-color: ${invert(labelTextColor)};
            box-shadow: ${constants.shadow3};
        `}
`;

const StyledPicture = styled(PictureDeprecated)`
    img {
        border-radius: ${BorderRadiusSmall};
    }
`;

export default function FullwidthConnectedContent({ model }: { model: ConnectedContentFullwidthModel }): JSX.Element {
    const {
        contentLabel,
        link,
        dataLayer,
        heading,
        headingMobile,
        text,
        textMobile,
        ctaText,
        image,
        designTemplate,
        openAsModalProactively,
        adformTracking
    } = model;
    const { labelTextColor } = designTemplate;
    const customAction = guardIsPageActionOfTypeCustomAction(link?.pageAction);
    const { openSurveyQuickViewBySurveyId } = useOpenSurveyQuickView();
    const imageRef = useRef<HTMLImageElement | null>(null);
    useAdformImpressionTracking({
        ref: imageRef,
        impTrackingUrls: adformTracking?.impTrackers
    });

    useEffect(() => {
        if (customAction && openAsModalProactively) {
            const surveyAction = CustomActionDiscriminator(
                customAction.action,
                () => null,
                () => null,
                () => null,
                (surveyModel) => surveyModel,
                () => null,
                () => null
            );

            if (surveyAction?.surveyId) openSurveyQuickViewBySurveyId({ surveyId: surveyAction.surveyId });
        }
    }, [link, openAsModalProactively]);

    const onClick = () => {
        track(dataLayer);

        if (adformTracking?.clickTracking) {
            postAdformClickTracking(adformTracking.clickTracking);
        }
    };

    if (heading || headingMobile || text || textMobile || ctaText) {
        return (
            <ThemeProvider theme={model.designThemeNameOverride as Theme}>
                <StaticWrapper>
                    <HorizontalLayout fullwidth model={model} onClick={onClick} />
                </StaticWrapper>
            </ThemeProvider>
        );
    }

    if (link?.pageAction) {
        return (
            <ThemeProvider theme={model.designThemeNameOverride as Theme}>
                <PageActionWrapper action={link?.pageAction} onClick={onClick}>
                    {contentLabel && <TopLabel labelTextColor={labelTextColor}>{contentLabel}</TopLabel>}
                    <StyledPicture ref={imageRef} {...image} />
                </PageActionWrapper>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={model.designThemeNameOverride as Theme}>
            <StaticWrapper onClick={onClick}>
                {contentLabel && <TopLabel labelTextColor={labelTextColor}>{contentLabel}</TopLabel>}
                <StyledPicture ref={imageRef} {...image} />
            </StaticWrapper>
        </ThemeProvider>
    );
}
